import React, { useEffect, useMemo, useRef, useState } from 'react';
import ToolLayout from 'tools/ToolLayout';
import FileDropZone from 'tools/FileDropZone';
import Colors from 'theme/colors';
import {
  btnDisabled,
  btnRow,
  card,
  chipScrollRow,
  chipStyle,
  dangerBtn,
  errorBox,
  hint,
  label,
  primaryBtn,
  privacy,
  secondaryBtn,
  textArea,
  warnBox,
} from 'tools/toolStyles';
import { downloadBlob, getWin } from 'tools/ffmpegShared';
import JsonTree, { ExpandMode, TreePick } from './JsonTree';
import {
  SAMPLE_JSON,
  copyText,
  countNodes,
  formatBytes,
  IndentChoice,
  indentString,
  isJsonStringPayload,
  lineColToOffset,
  minifyJson,
  parseJson,
  ParseResult,
  repairLooseJson,
  sortValue,
  stringifyJson,
  utf8Bytes,
} from './jsonUtils';

const MAX_WARN = 800 * 1024;
const MAX_REFUSE = 20 * 1024 * 1024;

type Pane = 'text' | 'tree';

function copyValueOf(value: any, indent: IndentChoice): string {
  if (value !== null && typeof value === 'object') return stringifyJson(value, indent);
  if (typeof value === 'string') return value;
  if (value === null) return 'null';
  return String(value);
}

function previewOf(value: any): string {
  if (value !== null && typeof value === 'object') {
    return Array.isArray(value) ? `Array(${value.length})` : `Object(${Object.keys(value).length})`;
  }
  if (typeof value === 'string') return JSON.stringify(value);
  if (value === null) return 'null';
  return String(value);
}

const JsonFormatter = () => {
  const win = getWin();
  const taRef = useRef<any>(null);
  const fileRef = useRef<any>(null);
  const [text, setText] = useState('');
  const [indent, setIndent] = useState<IndentChoice>(2);
  const [fileName, setFileName] = useState('data.json');
  const [search, setSearch] = useState('');
  const [expandMode, setExpandMode] = useState<ExpandMode>('default');
  const [expandNonce, setExpandNonce] = useState(0);
  const [copied, setCopied] = useState('');
  const [narrow, setNarrow] = useState(false);
  const [notice, setNotice] = useState('');
  const [parsed, setParsed] = useState<ParseResult>(() => parseJson(''));
  const [pane, setPane] = useState<Pane>('text');
  const [moreOpen, setMoreOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [textOpen, setTextOpen] = useState(true);
  const [optsOpen, setOptsOpen] = useState(false);
  const [pick, setPick] = useState<TreePick | null>(null);

  useEffect(() => {
    const measure = () => {
      const w = (win.innerWidth != null ? win.innerWidth : 800) as number;
      setNarrow(w < 720);
    };
    measure();
    if (win.addEventListener) {
      win.addEventListener('resize', measure);
      return () => win.removeEventListener('resize', measure);
    }
    return undefined;
  }, [win]);

  const bytes = useMemo(() => utf8Bytes(text), [text]);
  const tooBig = bytes > MAX_REFUSE;
  const bulky = bytes > MAX_WARN;

  useEffect(() => {
    const delay = bytes > MAX_WARN ? 400 : 140;
    const id = win.setTimeout(() => setParsed(parseJson(text)), delay);
    return () => {
      if (win.clearTimeout) win.clearTimeout(id);
    };
  }, [text, bytes, win]);

  const parsedValue = parsed.ok ? parsed.value : null;

  useEffect(() => {
    if (!parsed.ok) {
      setPick(null);
      return;
    }
    setPick((prev) => prev || { key: '$', path: '$', value: parsed.value });
  }, [parsed]);

  const nodeCount = useMemo(() => {
    if (!parsed.ok || bulky) return null;
    return countNodes(parsed.value);
  }, [parsed, bulky]);

  const flash = (msg: string) => {
    setCopied(msg);
    setNotice('');
    if (win.setTimeout) win.setTimeout(() => setCopied(''), 1800);
  };

  const applyText = (next: string, name?: string, showTree?: boolean) => {
    setText(next);
    if (name) setFileName(name);
    setNotice('');
    setPick(null);
    if (showTree && parseJson(next).ok) {
      setPane('tree');
      setTextOpen(false);
    }
  };

  const formatNow = (minify: boolean) => {
    if (tooBig) return;
    const result = parseJson(text);
    setParsed(result);
    if (!result.ok) {
      setNotice('');
      jumpToError(result.line, result.column, result.position);
      return;
    }
    applyText(minify ? minifyJson(result.value) : stringifyJson(result.value, indent), undefined, true);
  };

  const changeIndent = (n: IndentChoice) => {
    setIndent(n);
    const result = parseJson(text);
    if (result.ok) applyText(stringifyJson(result.value, n));
  };

  const sortNow = () => {
    if (!parsed.ok) return;
    applyText(stringifyJson(sortValue(parsed.value), indent), undefined, true);
  };

  const repairNow = () => {
    const repaired = repairLooseJson(text);
    const result = parseJson(repaired);
    if (result.ok) {
      applyText(stringifyJson(result.value, indent), undefined, true);
      flash('Đã sửa và làm đẹp');
    } else {
      applyText(repaired);
      setNotice('Đã thử sửa nhưng vẫn lỗi. Mở phần văn bản để xem.');
      setPane('text');
      setTextOpen(true);
    }
  };

  const unescapeNow = () => {
    const result = parseJson(text);
    if (!result.ok || !isJsonStringPayload(result.value)) {
      setNotice('Chỉ bỏ escape khi toàn bộ JSON là một chuỗi chứa JSON bên trong.');
      return;
    }
    const inner = parseJson(result.value);
    if (inner.ok) applyText(stringifyJson(inner.value, indent), undefined, true);
    else applyText(result.value);
  };

  const escapeNow = () => {
    applyText(JSON.stringify(text));
  };

  const jumpToError = (line: number | null, column: number | null, position: number | null) => {
    setPane('text');
    setTextOpen(true);
    const el = taRef.current;
    if (!el || !el.focus) return;
    let pos = position;
    if ((pos == null || isNaN(pos)) && line != null && column != null) {
      pos = lineColToOffset(text, line, column);
    }
    const applyCaret = () => {
      el.focus();
      if (pos != null && el.setSelectionRange) {
        const p = Math.max(0, Math.min(text.length, pos));
        el.setSelectionRange(p, Math.min(text.length, p + 1));
      }
      if (el.scrollIntoView) el.scrollIntoView({ block: 'center' });
    };
    applyCaret();
    if (win.setTimeout) win.setTimeout(applyCaret, 80);
  };

  const copyAll = async () => {
    if (!text) {
      flash('Chưa có JSON để copy');
      return;
    }
    const ok = await copyText(text, win);
    flash(ok ? 'Đã copy cả JSON' : 'Không copy được');
  };

  const copyPick = async (kind: 'key' | 'value') => {
    if (!pick) {
      flash('Bấm một dòng trên cây trước');
      return;
    }
    const payload = kind === 'key' ? pick.key : copyValueOf(pick.value, indent);
    const ok = await copyText(payload, win);
    flash(ok ? `Đã copy ${kind === 'key' ? 'key' : 'value'}` : 'Không copy được');
  };

  const pasteNow = async () => {
    try {
      if (!win.navigator || !win.navigator.clipboard || !win.navigator.clipboard.readText) {
        throw new Error('no clipboard');
      }
      const t = await win.navigator.clipboard.readText();
      if (!t) {
        setNotice('Clipboard trống. Dán bằng tay vào ô văn bản.');
        setPane('text');
        setTextOpen(true);
        if (taRef.current) taRef.current.focus();
        return;
      }
      applyText(t, undefined, true);
      flash('Đã dán');
    } catch {
      setPane('text');
      setTextOpen(true);
      setNotice('Trình duyệt chặn đọc clipboard. Bấm vào ô văn bản rồi dán (giữ để dán).');
      if (taRef.current) taRef.current.focus();
    }
  };

  const download = () => {
    if (!text) return;
    const blob = new win.Blob([text], { type: 'application/json;charset=utf-8' });
    const name = fileName && /\.json$/i.test(fileName) ? fileName : `${fileName || 'data'}.json`;
    downloadBlob(blob, name);
  };

  const onFiles = async (files: any[]) => {
    const f = files && files[0];
    if (!f) return;
    if (f.size > MAX_REFUSE) {
      setNotice(`File lớn hơn ${formatBytes(MAX_REFUSE)} — không mở để tránh treo máy.`);
      return;
    }
    try {
      const buf = await f.arrayBuffer();
      const decoder = new win.TextDecoder('utf-8', { fatal: false });
      const raw = decoder.decode(buf);
      setFileName(f.name || 'data.json');
      applyText(raw, f.name || 'data.json', true);
    } catch (err: any) {
      setNotice((err && err.message) || 'Không đọc được file.');
    }
  };

  const onFileInput = (e: any) => {
    const list = e.target && e.target.files;
    if (list) onFiles(list);
    if (e.target) e.target.value = '';
  };

  const onKeyDown = (e: any) => {
    const key = e.key;
    const mod = e.ctrlKey || e.metaKey;
    if (mod && key === 'Enter') {
      e.preventDefault();
      formatNow(false);
    }
    if (key === 'Tab' && !mod && !narrow) {
      e.preventDefault();
      const el = taRef.current;
      if (!el) return;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const tab = indentString(indent);
      const next = text.slice(0, start) + tab + text.slice(end);
      setText(next);
      if (win.requestAnimationFrame) {
        win.requestAnimationFrame(() => {
          el.selectionStart = el.selectionEnd = start + tab.length;
        });
      }
    }
  };

  const statusOk = parsed.ok && !!text.trim();
  let statusLine = 'Dán JSON rồi xem cây — bấm một dòng để copy key hoặc value.';
  if (!text.trim()) {
    statusLine = 'Dán JSON, mở file, hoặc bấm Ví dụ.';
  } else if (tooBig) {
    statusLine = `Quá lớn (${formatBytes(bytes)}). Hãy cắt nhỏ file.`;
  } else if (parsed.ok) {
    statusLine = `Hợp lệ · ${typeOf(parsed.value)}${nodeCount ? ` · ${nodeCount.keys} key` : ''} · ${formatBytes(bytes)}`;
  } else {
    statusLine = `Lỗi${parsed.line != null ? ` · dòng ${parsed.line}, cột ${parsed.column}` : ''} · ${parsed.message}`;
  }

  const editorMin = narrow ? Math.max(180, (win.innerHeight || 640) - 300) : 200;
  const editorStyle: any = {
    ...textArea,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 16,
    lineHeight: 1.45,
    minHeight: editorMin,
    tabSize: 2,
    WebkitOverflowScrolling: 'touch',
  };

  const flexBtn: any = { flex: 1, minWidth: 0 };

  const tabWrap: any = {
    display: 'flex',
    gap: 6,
    padding: 4,
    background: '#e8edf3',
    borderRadius: 12,
    marginBottom: 12,
  };

  const tabBtn = (active: boolean): any => ({
    ...secondaryBtn,
    flex: 1,
    backgroundColor: active ? Colors.MAIN_COLOR : 'transparent',
    color: active ? Colors.WHITE : Colors.TEXT,
    boxShadow: active ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
  });

  const foldBtn: any = {
    ...secondaryBtn,
    width: '100%',
    textAlign: 'left' as const,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: Colors.WHITE,
    marginBottom: 0,
  };

  const sticky: any = {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 20,
    padding: '10px 12px',
    paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))',
    background: 'rgba(240,243,247,0.97)',
    borderTop: `1px solid ${Colors.CARD_BORDER}`,
    boxShadow: '0 -6px 16px rgba(15,23,42,0.08)',
  };

  const toast: any = {
    position: 'fixed',
    left: 12,
    right: 12,
    bottom: narrow ? 84 : 24,
    zIndex: 40,
    textAlign: 'center' as const,
    background: '#1b5e20',
    color: '#fff',
    fontWeight: 700,
    fontSize: 14,
    padding: '12px 14px',
    borderRadius: 10,
    boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
    pointerEvents: 'none' as const,
  };

  const extraActions = (
    <>
      <label style={label}>Thụt dòng</label>
      <div style={chipScrollRow}>
        {([2, 3, 4, '\t'] as IndentChoice[]).map((n) => (
          <button key={String(n)} type="button" style={chipStyle(indent === n)} onClick={() => changeIndent(n)}>
            {n === '\t' ? 'Tab' : `${n} spaces`}
          </button>
        ))}
      </div>
      <div style={btnRow}>
        <button type="button" style={secondaryBtn} disabled={!text.trim() || tooBig} onClick={() => formatNow(false)}>
          Làm đẹp
        </button>
        <button type="button" style={secondaryBtn} disabled={!text.trim() || tooBig} onClick={() => formatNow(true)}>
          Minify
        </button>
        <button type="button" style={secondaryBtn} disabled={!parsed.ok} onClick={sortNow}>
          Sắp xếp key
        </button>
        <button type="button" style={secondaryBtn} disabled={!text.trim()} onClick={repairNow}>
          Thử sửa
        </button>
        <button type="button" style={secondaryBtn} disabled={!text} onClick={unescapeNow}>
          Bỏ escape
        </button>
        <button type="button" style={secondaryBtn} disabled={!text} onClick={escapeNow}>
          Escape
        </button>
        <button type="button" style={secondaryBtn} disabled={!text} onClick={copyAll}>
          Copy hết
        </button>
        <button type="button" style={secondaryBtn} disabled={!text} onClick={download}>
          Tải file
        </button>
        <button type="button" style={secondaryBtn} onClick={() => applyText(SAMPLE_JSON, 'sample.json', true)}>
          Ví dụ
        </button>
        <button
          type="button"
          style={text ? dangerBtn : btnDisabled(dangerBtn)}
          disabled={!text}
          onClick={() => applyText('')}
        >
          Xóa
        </button>
      </div>
    </>
  );

  const copyDock = (
    <div
      style={{
        background: '#f5f7fa',
        border: `1px solid ${Colors.CARD_BORDER}`,
        borderRadius: 10,
        padding: 10,
        marginBottom: 10,
      }}
    >
      {pick ? (
        <>
          <div style={{ fontSize: 12, color: Colors.MUTED_TEXT, marginBottom: 6, wordBreak: 'break-all' }}>
            {pick.path}
          </div>
          <div style={{ fontSize: 13, marginBottom: 8, wordBreak: 'break-word' }}>
            <b>{pick.key}</b>
            <span style={{ color: '#90a4ae' }}> = </span>
            <span>{previewOf(pick.value)}</span>
          </div>
        </>
      ) : (
        <p style={{ ...hint, margin: '0 0 8px' }}>Bấm một dòng trên cây, rồi copy key hoặc value tại đây.</p>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" style={{ ...primaryBtn, ...flexBtn, minHeight: 44 }} disabled={!pick} onClick={() => copyPick('key')}>
          Copy key
        </button>
        <button type="button" style={{ ...primaryBtn, ...flexBtn, minHeight: 44 }} disabled={!pick} onClick={() => copyPick('value')}>
          Copy value
        </button>
      </div>
    </div>
  );

  const textBody = (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' as const }}>
        <button type="button" style={{ ...secondaryBtn, padding: '8px 12px', minHeight: 40 }} onClick={pasteNow}>
          Dán
        </button>
        <button
          type="button"
          style={{ ...secondaryBtn, padding: '8px 12px', minHeight: 40 }}
          onClick={() => fileRef.current && fileRef.current.click()}
        >
          Mở file
        </button>
        <button
          type="button"
          style={{ ...secondaryBtn, padding: '8px 12px', minHeight: 40 }}
          disabled={!text.trim() || tooBig}
          onClick={() => formatNow(false)}
        >
          Làm đẹp
        </button>
      </div>
      {!narrow ? (
        <FileDropZone
          accept=".json,.txt,.log,application/json,text/plain"
          label="Kéo thả file JSON vào đây"
          hintText="Hoặc bấm để chọn — chỉ đọc trên máy bạn"
          onFiles={onFiles}
        />
      ) : null}
      <input
        ref={fileRef}
        type="file"
        accept=".json,.txt,.log,application/json,text/plain"
        onChange={onFileInput}
        style={{ display: 'none' }}
      />
      <textarea
        ref={taRef}
        style={{ ...editorStyle, marginTop: narrow ? 0 : 8 }}
        value={text}
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
        autoComplete="off"
        aria-label="JSON input"
        placeholder='Dán JSON vào đây. Ví dụ: {"ok": true}'
        onChange={(e: any) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => setEditing(true)}
        onBlur={() => setEditing(false)}
      />
    </>
  );

  const treeBody = parsed.ok && text.trim() ? (
    <>
      {narrow ? (
        <p style={{ ...hint, margin: '0 0 8px' }}>
          {pick ? `Đang chọn ${pick.path}` : 'Bấm một dòng, rồi Copy key / Copy value ở thanh dưới.'}
        </p>
      ) : (
        copyDock
      )}
      <div style={{ ...chipScrollRow, marginBottom: 8 }}>
        <button
          type="button"
          style={chipStyle(expandMode === 'all')}
          onClick={() => {
            if (bulky) {
              setNotice('File lớn: đừng mở hết cây. Hãy tìm hoặc mở từng nhánh.');
              return;
            }
            setExpandMode('all');
            setExpandNonce((n) => n + 1);
          }}
        >
          Mở hết
        </button>
        <button
          type="button"
          style={chipStyle(expandMode === 'none')}
          onClick={() => {
            setExpandMode('none');
            setExpandNonce((n) => n + 1);
          }}
        >
          Thu hết
        </button>
        <button
          type="button"
          style={chipStyle(expandMode === 'default')}
          onClick={() => {
            setExpandMode('default');
            setExpandNonce((n) => n + 1);
          }}
        >
          Mặc định
        </button>
      </div>
      <input
        style={{ ...textArea, minHeight: 44, marginBottom: 10, fontSize: 16 }}
        value={search}
        placeholder="Tìm key hoặc value"
        inputMode="search"
        autoComplete="off"
        onChange={(e: any) => setSearch(e.target.value)}
        onFocus={() => setEditing(true)}
        onBlur={() => setEditing(false)}
      />
      <div
        style={{
          maxHeight: narrow ? Math.max(280, (win.innerHeight || 640) - 300) : 560,
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <JsonTree
          value={parsedValue}
          indent={indent}
          search={search}
          expandMode={expandMode}
          expandNonce={expandNonce}
          selectedPath={pick ? pick.path : ''}
          onSelect={setPick}
          touch={narrow}
          openDepth={narrow ? 1 : 2}
        />
      </div>
    </>
  ) : (
    <div>
      <p style={{ ...hint, margin: '0 0 12px' }}>
        {text.trim() && !parsed.ok
          ? 'JSON đang lỗi — mở phần văn bản để sửa.'
          : 'Dán JSON hợp lệ để hiện cây. Bấm dòng để chọn, copy key hoặc value phía trên.'}
      </p>
      <button
        type="button"
        style={primaryBtn}
        onClick={() => {
          setPane('text');
          setTextOpen(true);
          if (text.trim() && !parsed.ok) jumpToError(parsed.line, parsed.column, parsed.position);
        }}
      >
        Mở văn bản
      </button>
    </div>
  );

  return (
    <ToolLayout title="JSON Formatter" wide>
      <p style={{ ...privacy, marginBottom: 10, padding: '8px 10px' }}>
        Xử lý trên máy bạn — không gửi lên language123.net.
      </p>

      <div
        style={
          text.trim() && !parsed.ok
            ? { ...errorBox, marginBottom: 10 }
            : statusOk
              ? { ...privacy, marginBottom: 10, padding: '8px 10px' }
              : { ...hint, marginBottom: 10 }
        }
      >
        {statusLine}
        {text.trim() && !parsed.ok && parsed.line != null ? (
          <button
            type="button"
            style={{ ...secondaryBtn, minHeight: 40, padding: '8px 12px', marginTop: 8, display: 'block' }}
            onClick={() => jumpToError(parsed.line, parsed.column, parsed.position)}
          >
            Nhảy tới lỗi
          </button>
        ) : null}
      </div>

      {notice ? <p style={warnBox}>{notice}</p> : null}
      {bulky && !tooBig ? (
        <p style={warnBox}>File khá lớn ({formatBytes(bytes)}). Cây thu gọn; minify nếu máy chậm.</p>
      ) : null}

      {narrow ? (
        <div style={tabWrap} role="tablist" aria-label="Chế độ xem">
          <button type="button" role="tab" aria-selected={pane === 'tree'} style={tabBtn(pane === 'tree')} onClick={() => setPane('tree')}>
            Cây JSON
          </button>
          <button type="button" role="tab" aria-selected={pane === 'text'} style={tabBtn(pane === 'text')} onClick={() => setPane('text')}>
            Văn bản
          </button>
        </div>
      ) : null}

      {narrow ? (
        pane === 'tree' ? (
          <div style={card}>{treeBody}</div>
        ) : (
          <div style={card}>{textBody}</div>
        )
      ) : (
        <>
          <div style={{ ...card, padding: 10, marginBottom: 10 }}>
            <button type="button" style={foldBtn} onClick={() => setTextOpen(!textOpen)} aria-expanded={textOpen}>
              <span>Văn bản JSON</span>
              <span style={{ color: Colors.MUTED_TEXT }}>{textOpen ? 'Đóng' : 'Mở'}</span>
            </button>
            {textOpen ? <div style={{ marginTop: 10 }}>{textBody}</div> : null}
          </div>

          <div style={{ ...card, padding: 10, marginBottom: 10 }}>
            <button type="button" style={foldBtn} onClick={() => setOptsOpen(!optsOpen)} aria-expanded={optsOpen}>
              <span>Tùy chọn khác</span>
              <span style={{ color: Colors.MUTED_TEXT }}>{optsOpen ? 'Đóng' : 'Mở'}</span>
            </button>
            {optsOpen ? <div style={{ marginTop: 10 }}>{extraActions}</div> : null}
          </div>

          <div style={card}>
            <div style={{ ...label, marginBottom: 10 }}>Cây JSON</div>
            {treeBody}
          </div>
        </>
      )}

      {narrow && !editing ? (
        <>
          <div style={{ height: 88 }} />
          <div style={sticky}>
            {moreOpen ? (
              <div
                style={{
                  ...card,
                  marginBottom: 10,
                  boxShadow: 'none',
                  maxHeight: '42vh',
                  overflow: 'auto',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                {extraActions}
              </div>
            ) : null}
            <div style={{ display: 'flex', gap: 8 }}>
              {pane === 'tree' ? (
                <>
                  <button type="button" style={{ ...primaryBtn, ...flexBtn }} disabled={!pick} onClick={() => copyPick('key')}>
                    Copy key
                  </button>
                  <button type="button" style={{ ...primaryBtn, ...flexBtn }} disabled={!pick} onClick={() => copyPick('value')}>
                    Copy value
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    style={{ ...primaryBtn, ...flexBtn }}
                    disabled={!text.trim() || tooBig}
                    onClick={() => formatNow(false)}
                  >
                    Làm đẹp
                  </button>
                  <button type="button" style={{ ...secondaryBtn, ...flexBtn }} onClick={pasteNow}>
                    Dán
                  </button>
                </>
              )}
              <button
                type="button"
                style={{ ...secondaryBtn, minWidth: 72 }}
                aria-expanded={moreOpen}
                onClick={() => setMoreOpen(!moreOpen)}
              >
                {moreOpen ? 'Đóng' : 'Thêm'}
              </button>
            </div>
          </div>
        </>
      ) : null}

      {copied ? (
        <div style={toast} role="status">
          {copied}
        </div>
      ) : null}
    </ToolLayout>
  );
};

function typeOf(value: any): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return `array[${value.length}]`;
  return typeof value;
}

export default JsonFormatter;
